import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { doc, setDoc, addDoc, collection, serverTimestamp, runTransaction, Timestamp } from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, auth, storage } from "../firebase";
import { colors, radius, spacing, typography } from "../utils/designTokens";
import { STATUS_LABELS } from "../utils/statusColors";
import { compressImage } from "../utils/imageCompress";
import AdminModal from "./AdminModal";
import FormField from "./FormField";
import AppSelect from "./AppSelect";
import StatusBadge from "./StatusBadge";
import { useToast } from "./Toast";
import {
  FaMapMarkerAlt, FaBuilding, FaExternalLinkAlt, FaStar, FaRegStickyNote, FaCheck, FaArrowLeft, FaLock, FaHandPointer, FaCamera, FaPen, FaFlag, FaCalculator, FaPlus, FaTrash, FaRegAddressCard, FaEnvelopeOpenText,
} from "react-icons/fa";
import { useIsMobile } from "../hooks/useResponsive";
import { useUser } from "../context/UserContext";
import {
  computeBloqueadoHasta, estaBloqueado, estaLibre, esTitular, puedeEditar,
  esGestion, fmtFechaLiberacion,
} from "../utils/crmAsignacion";
import { datosEdificio } from "../utils/datosEdificio";
import { ROLES_CONTACTO, contactosDeCrm, contactosLimpios } from "../utils/crmContactos";
import { useCotizadorPrefill } from "../context/CotizadorContext";

// Pipeline comercial (los estados viven en statusColors.js).
export const CRM_ESTADOS = [
  "sin_contactar", "contactado", "en_conversacion", "reunion", "propuesta", "ganado", "perdido", "descartado",
];
const PRIORIDADES = ["alta", "media", "baja"];

const fmt = (n) => (n == null || n === "" ? "—" : Number(n).toLocaleString("es-UY"));

// Los padrones fuera de Montevideo llevan id namespaceado (ej. "A-2698", Canelones):
// se persisten como string; los de MVD siguen siendo numéricos.
const padronKey = (p) => (Number.isFinite(Number(p)) ? Number(p) : String(p));

function scoreColor(v) {
  const n = Number(v) || 0;
  if (n >= 80) return colors.success;   // muy caliente
  if (n >= 60) return colors.primary;   // caliente
  if (n >= 40) return colors.warning;   // tibio
  return colors.gray600;                // frío
}

function fechaObraLabel(p) {
  if (!p) return "—";
  if (p.fechaAperturaObra) return `Obra aprobada ${p.fechaAperturaObra}`;
  if (p.anioObraFallback) return `Última obra (catastro): ${p.anioObraFallback}`;
  return "—";
}

const EMPTY = {
  estado: "sin_contactar", prioridad: "media", asignadoA: "", asignadoEmail: "",
  contactos: [],
  proximaAccion: "", proximaAccionFecha: "", proximaAccionHora: "",
};
const CONTACTO_VACIO = { nombre: "", rol: "", tel: "", email: "" };

/**
 * Ficha CRM de un prospecto (un padrón). Muestra el contexto del punto
 * (dirección, score, régimen, valor, obra) y deja al comercial editar estado,
 * prioridad, asignación, contacto, próxima acción y notas. Persiste en
 * `prospectos_crm/{padron}`.
 */
export default function CrmFicha({ open, onClose, padron, punto, crm, onSaved, comerciales = [], setSelected }) {
  const { showToast } = useToast();
  const isMobile = useIsMobile();
  const { setPrefill } = useCotizadorPrefill();
  const [form, setForm] = useState(EMPTY);
  const [notas, setNotas] = useState([]);
  const [nuevaNota, setNuevaNota] = useState("");
  const [saving, setSaving] = useState(false);
  const [fotos, setFotos] = useState([]);
  const [subiendoFoto, setSubiendoFoto] = useState(false);
  const fileInputRef = useRef(null);
  // Corrección de datos del edificio + reporte de duplicado (fuera del lock).
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [corr, setCorr] = useState({ direccion: "", nUnidades: "", anioConstruccion: "", nota: "" });
  const [corrSaving, setCorrSaving] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [dupDetalle, setDupDetalle] = useState("");
  const [dupSending, setDupSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Normaliza contactos (arreglo nuevo o contacto legacy); al menos una fila vacía para editar.
    const cs = contactosDeCrm(crm);
    setForm({ ...EMPTY, ...(crm || {}), contactos: cs.length ? cs : [{ ...CONTACTO_VACIO }] });
    setNotas(Array.isArray(crm?.notas) ? crm.notas : []);
    setFotos(Array.isArray(crm?.fotos) ? crm.fotos : []);
    setNuevaNota("");
    setCorrigiendo(false);
    setDupOpen(false);
    setDupDetalle("");
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, padron]);

  const email = auth.currentUser?.email || "comercial";
  const emailLc = String(email).toLowerCase();
  const { userType } = useUser();
  const isSuperAdmin = Number(userType) === 2;

  // Estado de asignación/lock (según lo GUARDADO en `crm`, no el form en edición).
  const nowMs = Date.now();
  const bloqueado = estaBloqueado(crm, nowMs);
  const titular = esTitular(crm, emailLc);
  const libre = estaLibre(crm, nowMs);
  const fechaLib = fmtFechaLiberacion(crm);
  const editable = puedeEditar(crm, { email: emailLc, userType }, nowMs);
  const miNombre = comerciales.find((c) => c.email === emailLc)?.name || email;

  const set = (k) => (e) => editable && setForm((f) => ({ ...f, [k]: e.target.value }));

  // Contactos del edificio (varios: presidente, portero, etc.).
  const MAX_CONTACTOS = 5;
  const updContacto = (i, k, v) => editable && setForm((f) => ({
    ...f, contactos: f.contactos.map((c, j) => (j === i ? { ...c, [k]: v } : c)),
  }));
  const addContacto = () => editable && setForm((f) => (
    f.contactos.length >= MAX_CONTACTOS ? f : { ...f, contactos: [...f.contactos, { ...CONTACTO_VACIO }] }
  ));
  const delContacto = (i) => editable && setForm((f) => {
    const next = f.contactos.filter((_, j) => j !== i);
    return { ...f, contactos: next.length ? next : [{ ...CONTACTO_VACIO }] };
  });

  // Catastro + correcciones de campo del comercial (overrides en prospectos_crm).
  const efectivo = datosEdificio(punto, crm);

  const mapsUrl = useMemo(() => {
    if (punto?.lat != null && punto?.lng != null) {
      return `https://www.google.com/maps/search/?api=1&query=${punto.lat},${punto.lng}`;
    }
    if (efectivo.direccion) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(efectivo.direccion + ", Montevideo")}`;
    return null;
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [punto, efectivo.direccion]);

  async function guardar() {
    if (!padron) return;
    if (!editable) {
      showToast("Este prospecto está asignado a otro agente.", "error");
      return;
    }
    setSaving(true);
    try {
      const nowTs = Timestamp.now();
      let notasFinal = notas;
      if (nuevaNota.trim()) {
        notasFinal = [...notas, { texto: nuevaNota.trim(), autor: email, at: new Date().toISOString() }];
      }

      // Auto-reclamo ("gestionar = tomar"): un comercial que guarda sobre un
      // prospecto libre sin haber apretado "Tomar" se lo asigna igual. Si no,
      // queda un prospecto gestionado pero sin dueño, que otro podría tomar.
      const autoReclamo = !isSuperAdmin && !form.asignadoEmail && estaLibre(crm, Date.now());
      const formFinal = autoReclamo
        ? { ...form, asignadoEmail: emailLc, asignadoA: miNombre }
        : form;

      // Contactos: guardo el arreglo limpio + espejo de los campos legacy
      // (contacto principal) para el código que aún los lee.
      const contactos = contactosLimpios(formFinal.contactos);
      const payload = {
        padron: padronKey(padron),
        ...formFinal,
        contactos,
        contactoNombre: contactos[0]?.nombre || "",
        contactoTel: contactos[0]?.tel || "",
        contactoEmail: contactos[0]?.email || "",
        notas: notasFinal,
        fotos,
        // denormalizado para listar el CRM sin cruzar puntos (prima la corrección de campo)
        direccion: crm?.datosCorregidos?.direccion || punto?.direccion || crm?.direccion || null,
        barrio: punto?.barrio || crm?.barrio || null,
        score: punto?.score ?? crm?.score ?? null,
        updatedAt: serverTimestamp(),
        updatedBy: email,
      };

      // Edificio MANUAL (alta desde el buscador del mapa): persiste sus
      // coordenadas y el flag para que el hook lo sintetice como punto del mapa.
      if (punto?.manual || crm?.manual) {
        payload.manual = true;
        payload.lat = punto?.lat ?? crm?.lat ?? null;
        payload.lng = punto?.lng ?? crm?.lng ?? null;
        if (punto?.nUnidades != null) payload.nUnidades = punto.nUnidades;
      }

      // ── Asignación + reloj de 60 días ──
      const prevEmail = (crm?.asignadoEmail || "");
      const newEmail = (formFinal.asignadoEmail || "");
      const asignacionCambio = newEmail !== prevEmail;
      const historialPrev = Array.isArray(crm?.asignacionHistorial) ? crm.asignacionHistorial : [];

      if (asignacionCambio && newEmail) {
        // (Re)asignación: fija asignadoAt, arranca/reinicia el reloj y registra en historial.
        const nombre = comerciales.find((c) => c.email === newEmail)?.name || formFinal.asignadoA || newEmail;
        const tipo = isSuperAdmin && prevEmail && estaBloqueado(crm, nowMs)
          ? "reasignacion_superadmin" : "asignacion";
        payload.asignadoEmail = newEmail;
        payload.asignadoA = nombre;
        payload.asignadoAt = nowTs;
        payload.ultimaGestionAt = nowTs;
        payload.bloqueadoHasta = computeBloqueadoHasta(nowTs.toDate());
        payload.asignacionHistorial = [...historialPrev,
          { email: newEmail, name: nombre, at: new Date().toISOString(), por: email, tipo }];
      } else if (asignacionCambio && !newEmail) {
        // Des-asignación manual (solo SuperAdmin) → liberar.
        payload.asignadoAt = null;
        payload.bloqueadoHasta = null;
        payload.liberadoAt = nowTs;
        payload.asignacionHistorial = [...historialPrev,
          { email: "", name: "", at: new Date().toISOString(), por: email, tipo: "liberacion_manual" }];
      } else if (newEmail && esGestion(crm, formFinal, nuevaNota)) {
        // Gestión concreta del titular (sin cambio de agente) → empuja el reloj +60 días.
        payload.ultimaGestionAt = nowTs;
        payload.bloqueadoHasta = computeBloqueadoHasta(nowTs.toDate());
      }

      const ref = doc(db, "prospectos_crm", String(padron));
      // Reclamo de comercial sobre un prospecto libre → transacción anti-concurrencia:
      // si otro agente lo tomó recién, se aborta (gana el registro cronológicamente anterior).
      const esReclamoComercial = !isSuperAdmin && asignacionCambio && !!newEmail;
      if (esReclamoComercial) {
        await runTransaction(db, async (tx) => {
          const snap = await tx.get(ref);
          const cur = snap.exists() ? snap.data() : {};
          if (!estaLibre(cur, Date.now())) throw new Error("OCUPADO");
          tx.set(ref, payload, { merge: true });
        });
      } else {
        await setDoc(ref, payload, { merge: true });
      }

      showToast(autoReclamo ? "Ficha guardada. El prospecto quedó asignado a vos (60 días)." : "Ficha guardada.", "success");
      onSaved && onSaved(padron, { ...payload, notas: notasFinal });
      onClose && onClose();
    } catch (e) {
      if (e?.message === "OCUPADO") {
        showToast("Otro agente tomó este prospecto recién. Refrescá para ver el estado.", "error");
      } else {
        showToast(e.message || "No se pudo guardar la ficha.", "error");
      }
    }
    setSaving(false);
  }

  function abrirCorreccion() {
    setCorr({
      direccion: efectivo.direccion || "",
      nUnidades: efectivo.nUnidades ?? "",
      anioConstruccion: efectivo.anioConstruccion ?? "",
      nota: crm?.datosCorregidos?.nota || "",
    });
    setDupOpen(false);
    setCorrigiendo(true);
  }

  // Corregir datos del edificio NO es gestión ni requiere el lock: es un dato
  // objetivo relevado en campo que le sirve a todo el equipo. El catastro
  // (prospeccion_puntos) queda intacto; el override vive en prospectos_crm.
  async function guardarCorreccion() {
    if (!padron) return;
    setCorrSaving(true);
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      // Solo cuenta como override lo que difiere del catastro; vacío o igual ⇒ null (vale el original).
      const datos = {
        direccion: corr.direccion.trim() && corr.direccion.trim() !== (punto?.direccion || "") ? corr.direccion.trim() : null,
        nUnidades: num(corr.nUnidades) != null && num(corr.nUnidades) !== (punto?.nUnidades ?? null) ? num(corr.nUnidades) : null,
        anioConstruccion: num(corr.anioConstruccion) != null && num(corr.anioConstruccion) !== (punto?.anioConstruccion ?? null) ? num(corr.anioConstruccion) : null,
        nota: corr.nota.trim() || null,
      };
      const payload = {
        padron: padronKey(padron),
        datosCorregidos: datos,
        datosCorregidosPor: email,
        datosCorregidosAt: serverTimestamp(),
        ...(datos.direccion ? { direccion: datos.direccion } : {}),
        updatedAt: serverTimestamp(),
        updatedBy: email,
      };
      await setDoc(doc(db, "prospectos_crm", String(padron)), payload, { merge: true });
      onSaved && onSaved(padron, { ...(crm || {}), ...payload, datosCorregidosAt: new Date().toISOString() });
      setCorrigiendo(false);
      showToast("Datos del edificio corregidos.", "success");
    } catch (e) {
      showToast(e.message || "No se pudo guardar la corrección.", "error");
    }
    setCorrSaving(false);
  }

  // Abre el Cotizador precargado con los datos efectivos del edificio; el
  // Cotizador guarda el presupuesto de vuelta en el prospecto ("Guardar en prospecto").
  // Datos efectivos del edificio (catastro/CRM) para precargar cualquier cotizador.
  function buildPrefill() {
    const barrio = punto?.barrio || crm?.barrio || null;
    const anio = Number(efectivo.anioConstruccion) || null;
    const regimen = punto?.estadoPH ? (String(punto.estadoPH).includes("ph") ? "PH" : "Común") : "";
    return {
      padron: padronKey(padron),
      direccion: efectivo.direccion || null,
      barrio,
      edificio: efectivo.direccion || `Padrón ${padron}`,
      ubicacion: barrio ? `${barrio}, Montevideo` : "Montevideo",
      apartamentos: Number(efectivo.nUnidades) || 0,
      antiguedadAnios: anio ? Math.max(0, new Date().getFullYear() - anio) : 0,
      // Catastro para la radiografía de la propuesta personalizada (Montevideo).
      anioConstruccion: anio || "",
      m2: punto?.totalM2 || punto?.areaEdif || "",
      regimen,
      ciudad: "Montevideo",
      jurisdiccion: "Montevideo",
      origen: "crm",
    };
  }

  // Abre el Cotizador completo (6 páginas) precargado.
  function cotizarHonorario() {
    setPrefill(buildPrefill());
    onClose && onClose();
    setSelected && setSelected("cotizador");
  }

  // Abre el Presupuesto exprés (1 hoja para cartas) precargado con el edificio.
  function presupuestoExpress() {
    setPrefill(buildPrefill());
    onClose && onClose();
    setSelected && setSelected("presupuestoexpress");
  }

  async function enviarReporteDuplicado() {
    if (!padron) return;
    setDupSending(true);
    try {
      await addDoc(collection(db, "prospeccion_reportes"), {
        padron: padronKey(padron),
        tipo: "duplicado",
        detalle: dupDetalle.trim(),
        direccion: efectivo.direccion || null,
        barrio: punto?.barrio || crm?.barrio || null,
        reportadoPor: miNombre,
        reportadoEmail: emailLc,
        createdAt: serverTimestamp(),
        estado: "pendiente",
      });
      setDupOpen(false);
      setDupDetalle("");
      showToast("Reporte enviado. Un superadmin lo va a revisar.", "success");
    } catch (e) {
      showToast(e.message || "No se pudo enviar el reporte.", "error");
    }
    setDupSending(false);
  }

  // Foto del edificio: se comprime, se sube a Storage y se persiste de una en el
  // doc CRM (merge) — si el comercial se olvida de "Guardar", la foto no se pierde.
  async function onFotoElegida(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // permite volver a sacar la misma foto
    if (!file || !padron) return;
    if (!editable) {
      showToast("Tomá el prospecto para poder gestionarlo.", "error");
      return;
    }
    setSubiendoFoto(true);
    try {
      const blob = await compressImage(file);
      const path = `prospeccion_fotos/${padron}/${Date.now()}.jpg`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, blob, { contentType: "image/jpeg" });
      const url = await getDownloadURL(sref);
      const nuevas = [...fotos, { url, path, autor: email, at: new Date().toISOString() }];
      setFotos(nuevas);
      await setDoc(
        doc(db, "prospectos_crm", String(padron)),
        { padron: padronKey(padron), fotos: nuevas, updatedAt: serverTimestamp(), updatedBy: email },
        { merge: true }
      );
      onSaved && onSaved(padron, { ...(crm || {}), fotos: nuevas });
      showToast("Foto guardada en el prospecto.", "success");
    } catch (err) {
      showToast(err?.message || "No se pudo subir la foto. Probá de nuevo.", "error");
    }
    setSubiendoFoto(false);
  }

  const footer = (
    <>
      <button onClick={onClose} style={btnGhost}>Cancelar</button>
      <button onClick={guardar} disabled={saving || !editable} style={btnPrimary}>
        <FaCheck size={12} /> {saving ? "Guardando…" : "Guardar ficha"}
      </button>
    </>
  );

  const notasOrden = [...notas].reverse();

  const body = (
    <>
      {/* Contexto del padrón (solo lectura) */}
      <div style={ctxBox}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" }}>
          {punto?.estadoPH && <StatusBadge status={punto.estadoPH} size="sm" />}
          {punto?.vis && <StatusBadge status="vivienda_promovida" label="Vivienda Promovida" size="sm" />}
          {punto?.yaAdministrado && <StatusBadge status="cuenta_activa" label="Ya administrado" size="sm" />}
          <div style={{ flex: 1 }} />
          <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: typography.sizes["2xl"], color: scoreColor(punto?.score) }}>
            <FaStar size={14} /> {punto?.score ?? "—"}
          </span>
        </div>
        <div style={ctxGrid}>
          <Ctx label="Dirección" value={efectivo.direccion || "—"} corregido={efectivo.corregido.direccion} />
          <Ctx label="Barrio" value={punto?.barrio || "—"} />
          <Ctx label="Obra" value={fechaObraLabel(punto)} />
          <Ctx label="Valor catastral" value={punto?.valorCatastral ? `$ ${fmt(punto.valorCatastral)}` : "—"} />
          <Ctx label="m² edificados" value={fmt(punto?.totalM2 || punto?.areaEdif)} />
          <Ctx label="Unidades" value={fmt(efectivo.nUnidades)} corregido={efectivo.corregido.nUnidades} />
          <Ctx label="Año construcción" value={efectivo.anioConstruccion || "—"} corregido={efectivo.corregido.anioConstruccion} />
          <Ctx label="Desarrollador (VP)" value={punto?.empresa || "—"} />
        </div>
        {efectivo.nota && (
          <div style={{ marginTop: spacing[2], fontSize: typography.sizes.xs, color: colors.gray600 }}>
            Nota de campo: {efectivo.nota}
          </div>
        )}
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noreferrer" style={mapsLink}>
            <FaMapMarkerAlt size={11} /> Ver ubicación en Google Maps <FaExternalLinkAlt size={9} />
          </a>
        )}
        <div style={{ display: "flex", gap: spacing[4], marginTop: spacing[3], flexWrap: "wrap" }}>
          {setSelected && (
            <button type="button" onClick={cotizarHonorario} style={{ ...ctxActionBtn, color: colors.primary }}>
              <FaCalculator size={10} /> Cotizar honorario
            </button>
          )}
          {setSelected && (
            <button type="button" onClick={presupuestoExpress} style={{ ...ctxActionBtn, color: colors.primary }}>
              <FaEnvelopeOpenText size={10} /> Presupuesto exprés
            </button>
          )}
          <button type="button" onClick={() => (corrigiendo ? setCorrigiendo(false) : abrirCorreccion())} style={ctxActionBtn}>
            <FaPen size={10} /> Corregir datos
          </button>
          <button type="button" onClick={() => { setCorrigiendo(false); setDupOpen((v) => !v); }} style={ctxActionBtn}>
            <FaFlag size={10} /> Marcar duplicado
          </button>
        </div>
        {corrigiendo && (
          <div style={corrBox}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: spacing[3] }}>
              <FormField label="Dirección real" htmlFor="corr-dir">
                <input id="corr-dir" value={corr.direccion} onChange={(e) => setCorr((c) => ({ ...c, direccion: e.target.value }))} placeholder="Ej. BENITO BLANCO 1234" style={inputStyle} />
              </FormField>
              <FormField label="Unidades" htmlFor="corr-uni">
                <input id="corr-uni" type="number" min="0" value={corr.nUnidades} onChange={(e) => setCorr((c) => ({ ...c, nUnidades: e.target.value }))} style={inputStyle} />
              </FormField>
              <FormField label="Año construcción" htmlFor="corr-anio">
                <input id="corr-anio" type="number" value={corr.anioConstruccion} onChange={(e) => setCorr((c) => ({ ...c, anioConstruccion: e.target.value }))} style={inputStyle} />
              </FormField>
              <FormField label="Nota (opcional)" htmlFor="corr-nota">
                <input id="corr-nota" value={corr.nota} onChange={(e) => setCorr((c) => ({ ...c, nota: e.target.value }))} placeholder="Ej. verificado en puerta" style={inputStyle} />
              </FormField>
            </div>
            <div style={{ display: "flex", gap: spacing[2], marginTop: spacing[3], alignItems: "center", flexWrap: "wrap" }}>
              <button type="button" onClick={guardarCorreccion} disabled={corrSaving} style={btnPrimary}>
                <FaCheck size={11} /> {corrSaving ? "Guardando…" : "Guardar corrección"}
              </button>
              <button type="button" onClick={() => setCorrigiendo(false)} style={btnGhost}>Cancelar</button>
              <span style={{ fontSize: typography.sizes.xs, color: colors.gray400 }}>El dato original del catastro no se pierde.</span>
            </div>
          </div>
        )}
        {dupOpen && (
          <div style={corrBox}>
            <FormField label="¿Por qué es un duplicado?" htmlFor="dup-det">
              <textarea id="dup-det" rows={2} value={dupDetalle} onChange={(e) => setDupDetalle(e.target.value)} placeholder="Ej. es el mismo edificio que el padrón 123456" style={{ ...inputStyle, resize: "vertical" }} />
            </FormField>
            <div style={{ display: "flex", gap: spacing[2], marginTop: spacing[3] }}>
              <button type="button" onClick={enviarReporteDuplicado} disabled={dupSending} style={btnPrimary}>
                <FaFlag size={11} /> {dupSending ? "Enviando…" : "Enviar reporte"}
              </button>
              <button type="button" onClick={() => setDupOpen(false)} style={btnGhost}>Cancelar</button>
            </div>
          </div>
        )}
      </div>

      {/* Aviso de solo-lectura: comercial viendo un prospecto asignado a otro (bloqueado). */}
      {!editable && (
        <div style={roBanner}>
          <FaLock size={12} /> Asignado a {crm?.asignadoA || "otro agente"} · se libera el {fechaLib || "—"}. Solo lectura hasta entonces.
        </div>
      )}

      {/* Campos editables del CRM */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[4], marginTop: spacing[5] }}>
        <FormField label="Estado">
          <AppSelect value={form.estado} ariaLabel="Estado" disabled={!editable}
            onChange={(v) => editable && setForm((f) => ({ ...f, estado: v }))}
            options={CRM_ESTADOS.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))} />
        </FormField>
        <FormField label="Prioridad">
          <AppSelect value={form.prioridad} ariaLabel="Prioridad" disabled={!editable}
            onChange={(v) => editable && setForm((f) => ({ ...f, prioridad: v }))}
            options={PRIORIDADES.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))} />
        </FormField>
        <FormField label="Asignado a">
          {isSuperAdmin ? (
            <>
              <AppSelect
                value={form.asignadoEmail || ""}
                ariaLabel="Asignado a"
                onChange={(em) => {
                  const c = comerciales.find((x) => x.email === em);
                  setForm((f) => ({ ...f, asignadoEmail: em, asignadoA: c ? c.name : "" }));
                }}
                options={[{ value: "", label: "— Sin asignar —" }, ...comerciales.map((c) => ({ value: c.email, label: c.name || c.email }))]}
              />
              {bloqueado && (
                <div style={lockNote}>
                  <FaLock size={10} /> Bloqueado para {crm?.asignadoA || "el agente"} hasta {fechaLib}. Reasignar queda registrado como override.
                </div>
              )}
            </>
          ) : titular ? (
            <div style={chipOk}>
              <FaCheck size={11} /> Asignado a vos{bloqueado ? ` · se libera el ${fechaLib}` : ""}
            </div>
          ) : libre ? (
            form.asignadoEmail === emailLc ? (
              <div style={chipOk}><FaCheck size={11} /> Te lo asignás al guardar</div>
            ) : (
              <button type="button" onClick={() => setForm((f) => ({ ...f, asignadoEmail: emailLc, asignadoA: miNombre }))} style={tomarBtn}>
                <FaHandPointer size={12} /> Tomar este prospecto
              </button>
            )
          ) : (
            <div style={chipLock}>
              <FaLock size={11} /> Asignado a {crm?.asignadoA || "otro agente"} · se libera el {fechaLib}
            </div>
          )}
        </FormField>

        <FormField label="Próxima acción" htmlFor="crm-prox" fullWidth>
          <input id="crm-prox" value={form.proximaAccion} onChange={set("proximaAccion")} disabled={!editable} placeholder="Ej. llamar al administrador, mandar propuesta…" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Fecha próxima acción" htmlFor="crm-proxf">
          <input id="crm-proxf" type="date" value={form.proximaAccionFecha || ""} onChange={set("proximaAccionFecha")} disabled={!editable} style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Hora (opcional)" htmlFor="crm-proxh">
          <input id="crm-proxh" type="time" value={form.proximaAccionHora || ""} onChange={set("proximaAccionHora")} disabled={!editable} style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
      </div>

      {/* Contactos del edificio (varios: presidente, portero, propietario…) */}
      <div style={{ marginTop: spacing[5] }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[2] }}>
          <FaRegAddressCard size={13} color={colors.gray400} />
          <span style={{ fontSize: typography.sizes.base, color: colors.gray700 }}>Contactos del edificio</span>
        </div>
        <div style={{ display: "grid", gap: spacing[2] }}>
          {form.contactos.map((ct, i) => (
            <div key={i} style={contactoBox}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[2] }}>
                <input value={ct.nombre} onChange={(e) => updContacto(i, "nombre", e.target.value)} disabled={!editable} placeholder="Nombre y apellido" aria-label={`Contacto ${i + 1} — nombre`} style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
                <AppSelect value={ct.rol} ariaLabel={`Contacto ${i + 1} — rol`} disabled={!editable}
                  onChange={(v) => updContacto(i, "rol", v)} options={ROLES_CONTACTO} />
                <input value={ct.tel} onChange={(e) => updContacto(i, "tel", e.target.value)} disabled={!editable} placeholder="09x xxx xxx" aria-label={`Contacto ${i + 1} — teléfono`} style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
                <input type="email" value={ct.email} onChange={(e) => updContacto(i, "email", e.target.value)} disabled={!editable} placeholder="mail@ejemplo.com" aria-label={`Contacto ${i + 1} — email`} style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
              </div>
              {editable && form.contactos.length > 1 && (
                <button type="button" onClick={() => delContacto(i)} style={contactoDelBtn} aria-label={`Quitar contacto ${i + 1}`}>
                  <FaTrash size={11} /> Quitar
                </button>
              )}
            </div>
          ))}
        </div>
        {editable && form.contactos.length < MAX_CONTACTOS && (
          <button type="button" onClick={addContacto} style={contactoAddBtn}>
            <FaPlus size={11} /> Agregar contacto
          </button>
        )}
      </div>

      {/* Notas */}
      <div style={{ marginTop: spacing[5] }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[2] }}>
          <FaRegStickyNote size={13} color={colors.gray400} />
          <span style={{ fontSize: typography.sizes.base, color: colors.gray700 }}>Notas de gestión</span>
        </div>
        <div style={{ display: "flex", gap: spacing[2] }}>
          <textarea
            value={nuevaNota}
            onChange={(e) => editable && setNuevaNota(e.target.value)}
            disabled={!editable}
            placeholder={editable ? "Agregar nota (quedará registrada con tu usuario y fecha)…" : "Tomá el prospecto para poder gestionarlo."}
            rows={2}
            style={{ ...inputStyle, flex: 1, resize: "vertical", fontFamily: typography.fontFamily, ...(editable ? {} : inputRO) }}
          />
        </div>
        {notasOrden.length > 0 && (
          <div style={{ marginTop: spacing[3], display: "grid", gap: spacing[2] }}>
            {notasOrden.map((n, i) => (
              <div key={i} style={notaItem}>
                <div style={{ fontSize: typography.sizes.base, color: colors.gray700, whiteSpace: "pre-wrap" }}>{n.texto}</div>
                <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: 2 }}>
                  {n.autor} · {n.at ? new Date(n.at).toLocaleString("es-UY") : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Fotos del edificio — respaldo visual de con qué edificio fue el contacto */}
      <div style={{ marginTop: spacing[5] }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[2] }}>
          <FaCamera size={13} color={colors.gray400} />
          <span style={{ fontSize: typography.sizes.base, color: colors.gray700 }}>Fotos del edificio</span>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onFotoElegida}
          style={{ display: "none" }}
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={!editable || subiendoFoto}
          style={{ ...fotoBtn, ...(editable ? {} : { opacity: 0.55, cursor: "not-allowed" }) }}
        >
          <FaCamera size={13} /> {subiendoFoto ? "Subiendo foto…" : "Sacar foto del edificio"}
        </button>
        <p style={{ margin: `${spacing[2]}px 0 0`, fontSize: typography.sizes.xs, color: colors.gray400 }}>
          Queda guardada al instante con tu usuario y fecha — respaldo de con qué edificio fue el contacto.
        </p>
        {fotos.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: spacing[2], marginTop: spacing[3] }}>
            {[...fotos].reverse().map((f, i) => (
              <a key={f.path || i} href={f.url} target="_blank" rel="noreferrer" style={{ display: "block", textDecoration: "none" }}>
                <img src={f.url} alt={`Foto del edificio ${fotos.length - i}`} loading="lazy" style={fotoThumb} />
                <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: 2, textAlign: "center" }}>
                  {f.at ? new Date(f.at).toLocaleDateString("es-UY") : ""}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </>
  );

  if (!open) return null;

  // Mobile: pantalla completa con botón "Volver" (estilo Habitia), no modal emergente.
  // Se portalea a <body>: dentro del scroller del shell (-webkit-overflow-scrolling)
  // la ficha quedaba atrapada en su stacking context y el FAB "Menú" flotaba por
  // encima tapando el botón Guardar.
  if (isMobile) {
    return createPortal(
      <div style={fsWrap}>
        <div style={fsHeader}>
          <button onClick={onClose} style={backBtn} aria-label="Volver">
            <FaArrowLeft size={13} /> Volver
          </button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {efectivo.direccion || `Padrón ${padron ?? "—"}`}
            </div>
            <div style={{ fontSize: typography.sizes.xs, color: colors.gray400 }}>Padrón {padron ?? "—"}</div>
          </div>
        </div>
        <div style={fsBody}>{body}</div>
        <div style={fsFooter}>
          <button onClick={onClose} style={{ ...btnGhost, flex: 1, justifyContent: "center" }}>Cancelar</button>
          <button onClick={guardar} disabled={saving || !editable} style={{ ...btnPrimary, flex: 1, justifyContent: "center" }}>
            <FaCheck size={12} /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>,
      document.body
    );
  }

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      size="wide"
      title={`Prospecto · Padrón ${padron ?? "—"}`}
      subtitle={efectivo.direccion || undefined}
      icon={<FaBuilding size={18} />}
      footer={footer}
    >
      {body}
    </AdminModal>
  );
}

function Ctx({ label, value, corregido }) {
  return (
    <div>
      <div style={{ fontSize: typography.sizes.xs, color: colors.gray400 }}>
        {label}{corregido && <span style={{ color: colors.primary }}> · corregido</span>}
      </div>
      <div style={{ fontSize: typography.sizes.base, color: colors.gray700 }}>{value}</div>
    </div>
  );
}

const ctxBox = { padding: spacing[4], background: colors.gray50, borderRadius: radius.md };
const ctxActionBtn = { display: "inline-flex", alignItems: "center", gap: 5, padding: 0, border: "none", background: "transparent", fontSize: typography.sizes.sm, color: colors.gray600, cursor: "pointer", fontFamily: "inherit", transition: "color 0.15s ease" };
const corrBox = { marginTop: spacing[3], padding: spacing[3], background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm };
const ctxGrid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: spacing[3], marginTop: spacing[3] };
const mapsLink = { display: "inline-flex", alignItems: "center", gap: 5, marginTop: spacing[3], fontSize: typography.sizes.sm, color: colors.primary, textDecoration: "none" };
const notaItem = { padding: spacing[3], background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm };
const contactoBox = { padding: spacing[3], background: colors.gray50, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, display: "grid", gap: spacing[2] };
const contactoAddBtn = { display: "inline-flex", alignItems: "center", gap: 6, marginTop: spacing[2], padding: "8px 12px", border: `1px solid ${colors.primary}`, borderRadius: radius.sm, background: colors.primaryLighter, color: colors.primary, cursor: "pointer", fontSize: typography.sizes.sm, fontFamily: "inherit" };
const contactoDelBtn = { display: "inline-flex", alignItems: "center", gap: 5, justifySelf: "start", padding: "4px 8px", border: "none", borderRadius: radius.sm, background: "transparent", color: colors.error, cursor: "pointer", fontSize: typography.sizes.xs, fontFamily: "inherit" };
const fotoBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "11px 12px", minHeight: 44, boxSizing: "border-box", borderRadius: radius.sm, border: `1px solid ${colors.primary}`, background: colors.primaryLighter, color: colors.primary, cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit" };
const fotoThumb = { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: radius.sm, border: `1px solid ${colors.gray200}`, display: "block" };
const inputStyle = {
  width: "100%", padding: "11px 12px", minHeight: 44,
  border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
  fontSize: typography.sizes.base, fontFamily: typography.fontFamily,
  background: colors.white, color: colors.gray900, boxSizing: "border-box",
};
// Estados de asignación / lock del CRM comercial.
const inputRO = { background: colors.gray50, color: colors.gray600, cursor: "not-allowed" };
const roBanner = { display: "flex", alignItems: "center", gap: 6, marginTop: spacing[4], padding: "10px 12px", borderRadius: radius.sm, background: colors.gray100, color: colors.gray700, fontSize: typography.sizes.sm };
const lockNote = { display: "flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: typography.sizes.xs, color: colors.warningDark };
const chipOk = { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 12px", minHeight: 42, boxSizing: "border-box", borderRadius: radius.sm, background: colors.successLight, color: colors.successDark, fontSize: typography.sizes.sm };
const chipLock = { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 12px", minHeight: 42, boxSizing: "border-box", borderRadius: radius.sm, background: colors.gray100, color: colors.gray700, fontSize: typography.sizes.sm };
const tomarBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: "10px 12px", minHeight: 42, boxSizing: "border-box", borderRadius: radius.sm, border: `1px solid ${colors.primary}`, background: colors.primaryLighter, color: colors.primary, cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit" };
// Ficha full-screen mobile
const fsWrap = { position: "fixed", inset: 0, zIndex: 2000, background: colors.white, display: "flex", flexDirection: "column" };
const fsHeader = { display: "flex", alignItems: "center", gap: spacing[3], padding: `${spacing[3]}px ${spacing[4]}px`, borderBottom: `1px solid ${colors.gray200}`, flexShrink: 0 };
const backBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", background: colors.primaryLighter, color: colors.primary, border: "none", borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit", flexShrink: 0 };
const fsBody = { flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: spacing[4] };
const fsFooter = { display: "flex", gap: spacing[2], padding: spacing[3], paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))", borderTop: `1px solid ${colors.gray200}`, background: colors.white, flexShrink: 0 };
const btnGhost = {
  padding: `${spacing[2]}px ${spacing[4]}px`, background: colors.white,
  border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, cursor: "pointer",
  fontSize: typography.sizes.base, color: colors.gray700,
};
const btnPrimary = {
  display: "inline-flex", alignItems: "center", gap: spacing[1],
  padding: `${spacing[2]}px ${spacing[4]}px`, background: colors.primary, color: colors.white,
  border: "none", borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.base,
};
